import { Injectable, inject } from '@angular/core';
import { getAuth } from 'firebase/auth';
import { doc, getFirestore, updateDoc } from 'firebase/firestore';
import { combineLatest, debounceTime, distinctUntilChanged, map, skip } from 'rxjs';

import { SidenavService } from '../../../shared/sidenav/sidenav.service';
import { SettingsModel } from '../settings.model';
import { NotificationService } from './notification.service';
import { ThemeService } from './theme.service';

// Saves the settings of this browser in users/{uid}.settings whenever one changes (Settings screen,
// theme button, sidenav toggles), so the next sign-in, on any device, restores the same choices.
@Injectable({
  providedIn: 'root'
})
export class UserSettingsService {

  private theme = inject(ThemeService);
  private sidenav = inject(SidenavService);
  private notification = inject(NotificationService);
  private started = false;

  // Called once by AppComponent
  start(): void {
    if (this.started) return;
    this.started = true;

    combineLatest([
      this.theme.mode$(),
      this.sidenav.sidenavStatus(),
      this.sidenav.sidenavMode(),
      this.sidenav.sidenavSize(),
      this.notification.notificationStatus()
    ]).pipe(
      map(([theme, status, type, size, notification]): SettingsModel => ({
        theme,
        sidenav: { type, size, status },
        notification: notification ? 'on' : 'off'
      })),
      distinctUntilChanged((a, b) => JSON.stringify(a) == JSON.stringify(b)),
      // The values read at startup are not a change
      skip(1),
      // Several services change together at sign-in and when the sidenav mode opens it
      debounceTime(500)
    ).subscribe(settings => this.save(settings));
  }

  private async save(settings: SettingsModel): Promise<void> {
    const user = getAuth().currentUser;
    // Signed out (e.g. theme changed on the sign-in page): kept only in this browser
    if (!user?.emailVerified) return;

    try {
      await updateDoc(doc(getFirestore(), 'users', user.uid), { settings });
    } catch (error) {
      console.error('UserSettingsService -> save', error);
    }
  }
}

// Settings saved before the sidenav size values were aligned ("collapsed" instead of "collaped")
export function normalizeSettings(settings: SettingsModel): SettingsModel {
  const size = settings.sidenav?.size == 'sidenav' ? 'sidenav' : 'collaped';
  return { ...settings, sidenav: { ...settings.sidenav, size } };
}
